import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, MenuItem } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { ReactElement, SyntheticEvent, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { QueryFeature } from '../../api/schema';
import { getFeatures, getFeaturesFetching, selectFeaturesList } from '../../redux/slices/features/featuresSlice';
import { addFeaturesForOrganization, getFeaturesForOrganization } from '../../redux/slices/organizations/organizationsSlice';
import { useDispatch } from '../../redux/store';
import { TextField } from '../../styled/inputs';
import { GetValidationErrors } from '../../tool/validation';

interface IProp {
  featuresExclude?: QueryFeature[];
  onClose: (redirectURL?: string) => void;
  open: boolean;
  organizationId?: string;
}

interface Values {
  id: string;
}

const schema = yup.object().shape({
  id: yup.string().required('Required field'),
});

export const FeatureForm = ({ onClose, featuresExclude, open, organizationId }: IProp): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const featuresExcludeIds = featuresExclude?.map((feature) => feature.id);

  const features = useSelector(selectFeaturesList({}));
  const fetching = useSelector(getFeaturesFetching);

  const manufacturersToUse = features?.result?.filter((feature) => !featuresExcludeIds?.includes(feature.id));

  useEffect(() => {
    if (organizationId) {
      dispatch(getFeatures({}));
    }
  }, [dispatch, organizationId]);

  const submit = async (values: Values, { setSubmitting, setErrors }: FormikHelpers<Values>) => {
    try {
      setSubmitting(true);

      if (organizationId) {
        await dispatch(addFeaturesForOrganization({ organizationId, features: { add: [values.id] } })).unwrap();
        await dispatch(getFeaturesForOrganization(organizationId));
      }
      onClose();
      // eslint-disable-next-line
    } catch (e: any) {
      setErrors(GetValidationErrors(e.response.status, navigate, e.response.data.messages));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <Formik
        validationSchema={schema}
        onSubmit={submit}
        initialValues={{
          id: '',
        }}
      >
        {({
          handleSubmit,
          handleChange,
          // handleBlur,
          values,
          touched,
          // isValid,
          errors,
          isSubmitting,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogTitle>Add Feature</DialogTitle>
            <DialogContent>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <TextField
                  select
                  name="id"
                  label="Feature"
                  error={Boolean(touched.id && errors.id)}
                  fullWidth
                  value={values.id}
                  helperText={touched.id && errors.id}
                  onChange={handleChange}
                  variant="outlined"
                  my={2}
                  required
                >
                  {manufacturersToUse?.map((option) => (
                    <MenuItem key={option.id?.toString()} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 6, pb: 6 }}>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={fetching || isSubmitting}>
                Add
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
